import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { X, Tv } from "lucide-react";
import { userApi, type PublicRelease } from "../lib/api";
import { useI18n } from "../i18n";

// ==========================================
// 平台按钮专属矢量图标
// ==========================================
function WindowsIcon() {
  return (
    <svg viewBox="0 0 24 24" width="18" height="18" fill="currentColor" aria-hidden="true">
      <path d="M0 3.449L9.75 2.1v9.451H0m10.949-9.602L24 0v11.551H10.949M0 12.6h9.75v9.451L0 20.699M10.949 12.6H24V24l-13.051-1.802" />
    </svg>
  );
}

function AppleIcon() {
  return (
    <svg viewBox="0 0 24 24" width="18" height="18" fill="currentColor" aria-hidden="true">
      <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.81-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M15.97 6.37c.61-.75 1.04-1.8 0.91-2.87-.93.04-2.02.63-2.67 1.38-.56.65-1.05 1.71-.92 2.74 1.05.08 2.07-.53 2.68-1.25z" />
    </svg>
  );
}

function AndroidIcon() {
  return (
    <svg viewBox="0 0 24 24" width="18" height="18" fill="currentColor" aria-hidden="true">
      <path d="M6 18c0 .55.45 1 1 1h1v3.5c0 .83.67 1.5 1.5 1.5s1.5-.67 1.5-1.5V19h2v3.5c0 .83.67 1.5 1.5 1.5s1.5-.67 1.5-1.5V19h1c.55 0 1-.45 1-1V8H6v10zM3.5 8C2.67 8 2 8.67 2 9.5v6c0 .83.67 1.5 1.5 1.5S5 16.33 5 15.5v-6C5 8.67 4.33 8 3.5 8zm17 0c-.83 0-1.5.67-1.5 1.5v6c0 .83.67 1.5 1.5 1.5s1.5-.67 1.5-1.5v-6c0-.83-.67-1.5-1.5-1.5zm-4.97-4.84l1.3-1.3c.2-.2.2-.51 0-.71-.2-.2-.51-.2-.71 0l-1.48 1.48C13.72 2.24 12.88 2 12 2c-.88 0-1.72.24-2.64.63L7.88.15c-.2-.2-.51-.2-.71 0-.2.2-.2.51 0 .71l1.3 1.3C6.73 3.25 5.5 5.01 5.5 7h13c0-1.99-1.23-3.75-2.97-4.84zM9 5.5c-.41 0-.75-.34-.75-.75s.34-.75.75-.75.75.34.75.75-.34.75-.75.75zm6 0c-.41 0-.75-.34-.75-.75s.34-.75.75-.75.75.34.75.75-.34.75-.75.75z" />
    </svg>
  );
}

function TvIcon() {
  return (
    <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <rect x="2" y="7" width="20" height="14" rx="2" ry="2" />
      <path d="M17 2l-5 5-5-5" />
    </svg>
  );
}

// ==========================================
// Hero 左侧多设备与氛围矢量图（品牌橙色主题调性）
// ==========================================
function HeroVisualIllustration() {
  return (
    <div className="dm-hero-scene" aria-hidden="true">
      <div className="dm-hero-backdrop" />
      
      {/* 主屏幕 */}
      <div className="dm-hero-screen-base">
        <div className="dm-hero-screen-top">
          <span />
          <span />
          <span />
        </div>
        <div className="dm-hero-screen-content">
          <div className="dm-hero-inner-card" />
          <div className="dm-hero-inner-card" />
        </div>
      </div>

      {/* 手机前景浮动 */}
      <div className="dm-hero-phone-floating">
        <div className="dm-hero-phone-speaker" />
        <div className="dm-hero-phone-display">
          <div className="dm-hero-phone-glow" />
        </div>
      </div>

      {/* 浮动徽章：火箭、影音、游戏、开关 */}
      <div className="dm-floating-badge dm-badge-rocket">
        <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M4.5 16.5c-1.5 1.26-2 5-2 5s3.74-.5 5-2c.71-.84.7-2.13-.09-2.91a2.18 2.18 0 0 0-2.91-.09z" />
          <path d="m12 15-3-3a22 22 0 0 1 2-3.95A12.88 12.88 0 0 1 22 2c0 2.72-.78 7.5-6 11a22.35 22.35 0 0 1-4 2z" />
        </svg>
      </div>

      <div className="dm-floating-badge dm-badge-film">
        <svg viewBox="0 0 24 24" width="18" height="18" fill="currentColor">
          <path d="M18 4l2 4h-3l-2-4h-2l2 4h-3l-2-4H8l2 4H7L5 4H4c-1.1 0-1.99.9-1.99 2L2 18c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V4h-4z" />
        </svg>
      </div>

      <div className="dm-floating-badge dm-badge-game">
        <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
          <line x1="6" y1="12" x2="10" y2="12" />
          <line x1="8" y1="10" x2="8" y2="14" />
          <line x1="15" y1="13" x2="15.01" y2="13" />
          <line x1="18" y1="11" x2="18.01" y2="11" />
          <rect x="2" y="6" width="20" height="12" rx="4" />
        </svg>
      </div>

      <div className="dm-floating-badge dm-badge-power">
        <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
          <path d="M18.36 6.64a9 9 0 1 1-12.73 0M12 2v10" />
        </svg>
      </div>
    </div>
  );
}

// ==========================================
// 下载页面主体组件
// ==========================================
export function DownloadPage() {
  const [releases, setReleases] = useState<PublicRelease[]>([]);
  const [tvModalOpen, setTvModalOpen] = useState(false);
  const { t } = useI18n();

  useEffect(() => {
    let active = true;
    void userApi.releases().then((items) => {
      if (active) setReleases(items);
    }).catch(() => {
      if (active) setReleases([]);
    });
    return () => { active = false; };
  }, []);

  // 匹配各平台已发布安装包或官方渠道
  const windowsRelease = releases.find((r) => r.platform === "windows");
  const macosRelease = releases.find((r) => r.platform === "macos");
  const androidRelease = releases.find((r) => r.platform === "android");
  const iosRelease = releases.find((r) => r.platform === "ios");
  const tvRelease = releases.find((r) => r.platform === "tv");

  const windowsUrl = windowsRelease?.downloadUrl || "https://github.com/clash-verge-rev/clash-verge-rev/releases/latest";
  const macosUrl = macosRelease?.downloadUrl || "https://github.com/clash-verge-rev/clash-verge-rev/releases/latest";
  const androidUrl = androidRelease?.downloadUrl || "https://github.com/MetaCubeX/ClashMetaForAndroid/releases";
  const iosUrl = iosRelease?.downloadUrl || "https://apps.apple.com";

  const handleTvClick = (e: React.MouseEvent) => {
    if (!tvRelease?.downloadUrl) {
      e.preventDefault();
      setTvModalOpen(true);
    }
  };

  return (
    <>
      {/* 顶部 Hero 区域：图二布局风格（左侧场景插画，右侧加速器标语） */}
      <section className="download-hero-section">
        <div className="container download-hero-container">
          <div className="download-hero-visual">
            <HeroVisualIllustration />
          </div>
          <div className="download-hero-copy">
            <span className="download-hero-badge">{t("官方客户端")}</span>
            <h1>{t("海外华人回国加速器")}</h1>
            <p>{t("独创边缘节点加速技术，畅享国内网络")}</p>
          </div>
        </div>
      </section>

      {/* 3 张下载卡片：电脑端、手机端、电视端 */}
      <section className="download-showcase-section">
        <div className="container download-cards-grid">
          
          {/* 卡片 1: Flux cloud 电脑端 */}
          <article className="download-device-card">
            <div className="download-mockup-wrapper">
              <div className="download-mockup-box">
                <img
                  src="/images/download-desktop.jpg"
                  alt={t("Flux cloud 电脑端")}
                  className="download-mockup-img"
                  loading="eager"
                />
              </div>
            </div>
            <div className="download-card-body">
              <h2 className="download-card-title">{t("Flux cloud 电脑端")}</h2>
              <div className="download-card-actions">
                <a
                  className="download-platform-button"
                  href={windowsUrl}
                  target="_blank"
                  rel="noreferrer"
                >
                  <WindowsIcon />
                  <span>{t("Windows 版下载")}</span>
                </a>
                <a
                  className="download-platform-button"
                  href={macosUrl}
                  target="_blank"
                  rel="noreferrer"
                >
                  <AppleIcon />
                  <span>{t("Mac 版下载")}</span>
                </a>
              </div>
            </div>
          </article>

          {/* 卡片 2: Flux cloud 手机端 */}
          <article className="download-device-card">
            <div className="download-mockup-wrapper">
              <div className="download-mockup-box">
                <img
                  src="/images/download-mobile.jpg"
                  alt={t("Flux cloud 手机端")}
                  className="download-mockup-img"
                  loading="eager"
                />
              </div>
            </div>
            <div className="download-card-body">
              <h2 className="download-card-title">{t("Flux cloud 手机端")}</h2>
              <div className="download-card-actions">
                <a
                  className="download-platform-button"
                  href={androidUrl}
                  target="_blank"
                  rel="noreferrer"
                >
                  <AndroidIcon />
                  <span>{t("Android 版下载")}</span>
                </a>
                <a
                  className="download-platform-button"
                  href={iosUrl}
                  target="_blank"
                  rel="noreferrer"
                >
                  <AppleIcon />
                  <span>{t("iOS 版下载")}</span>
                </a>
              </div>
            </div>
          </article>

          {/* 卡片 3: Flux cloud 电视端 */}
          <article className="download-device-card">
            <div className="download-mockup-wrapper">
              <div className="download-mockup-box">
                <img
                  src="/images/download-tv.jpg"
                  alt={t("Flux cloud 电视端")}
                  className="download-mockup-img"
                  loading="eager"
                />
              </div>
            </div>
            <div className="download-card-body">
              <h2 className="download-card-title">{t("Flux cloud 电视端")}</h2>
              <div className="download-card-actions">
                <a
                  className="download-platform-button"
                  href={tvRelease?.downloadUrl || "#tv-beta"}
                  onClick={handleTvClick}
                  target={tvRelease?.downloadUrl ? "_blank" : undefined}
                  rel="noreferrer"
                >
                  <TvIcon />
                  <span>{t("TV 公测版下载")}</span>
                </a>
              </div>
              <p className="download-card-hint">{t("支持 Android TV 与智能盒子")}</p>
            </div>
          </article>

        </div>
      </section>

      {/* TV 公测版说明弹窗 */}
      {tvModalOpen && (
        <div
          className="account-dialog-backdrop"
          onMouseDown={(e) => { if (e.target === e.currentTarget) setTvModalOpen(false); }}
        >
          <div className="account-settings-dialog" role="dialog" aria-modal="true">
            <header className="account-dialog-heading">
              <div>
                <span className="account-dialog-icon"><Tv aria-hidden="true" /></span>
                <div>
                  <h2>{t("TV 公测版申请")}</h2>
                  <p>{t("家庭大屏 4K 超清影音体验")}</p>
                </div>
              </div>
              <button
                type="button"
                className="account-dialog-close"
                onClick={() => setTvModalOpen(false)}
                aria-label={t("关闭")}
              >
                <X aria-hidden="true" />
              </button>
            </header>
            <div className="account-settings-panel">
              <p style={{ margin: "16px 0 24px", color: "#bbb", lineHeight: 1.7 }}>
                {t("TV 公测版安装包正在灰度分发中，可前往账户中心提交工单或联系客服获取内测资格。")}
              </p>
              <footer className="account-dialog-actions">
                <button
                  type="button"
                  className="account-secondary-button"
                  onClick={() => setTvModalOpen(false)}
                >
                  {t("我知道了")}
                </button>
                <Link
                  className="account-primary-button"
                  to="/account"
                  onClick={() => setTvModalOpen(false)}
                >
                  {t("前往账户中心")}
                </Link>
              </footer>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
